import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MobileBottomSheet from './MobileBottomSheet';

function NotificationBell({ notifications, onMarkAsRead, onNotificationClick }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Limiter à 10 notifications les plus récentes
  const recentNotifications = notifications.slice(0, 10);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'subscription_expiring':
        return '⚠️';
      case 'subscription_expired':
        return '🚫';
      case 'payment_success':
        return '✅';
      case 'payment_failed':
        return '❌';
      case 'new_track':
        return '🎵';
      default:
        return '🔔';
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    
    // Si c'est une notification de nouveau titre, rediriger vers la page artiste
    if (notification.type === 'new_track' && notification.artist_name && onNotificationClick) {
      onNotificationClick(notification);
      setIsOpen(false);
    }
  };

  const menuContent = (
    <>
      {notifications.length === 0 ? (
        <div className="px-4 py-8 text-center text-white/80">
          <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
          </svg>
          <p className="text-sm">{t('notifications.empty')}</p>
        </div>
      ) : (
        <div>
          <div className="px-4 py-2 border-b border-white/10">
            <h3 className="font-bold text-sm text-white/80">{t('notifications.title')}</h3>
          </div>
          {recentNotifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full px-4 py-3 text-left text-white/80 hover:bg-white/5 transition-colors border-b border-white/5 ${
                !notification.is_read ? 'bg-white/5' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm mb-1">{notification.message}</p>
                  <p className="text-xs text-white/60">
                    {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                {!notification.is_read && (
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0 mt-1"></span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
      >
        <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
        </svg>
        
        {/* Badge pour les notifications non lues */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown des notifications */}
      {isOpen && (
        isMobile ? (
          <MobileBottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
            {menuContent}
          </MobileBottomSheet>
        ) : (
          <div className="absolute right-0 top-12 w-80 bg-[#282828] rounded-lg shadow-2xl py-2 z-50 border border-white/10 max-h-96 overflow-y-auto">
            {menuContent}
          </div>
        )
      )}
    </div>
  );
}

export default NotificationBell;
